import { typeShell } from "./typeShell";

/**
 * AI Chat UI utility shells — replaces styles/pages/ai_chat.css.
 * Outer page chrome uses pageShell; field look uses formShell.
 */

const msgBase =
    "relative max-w-[95%] break-words rounded-lg p-2 text-text-primary [overflow-wrap:anywhere] bp-md:max-w-[90%] bp-md:px-3";

const msgContent =
    "break-words text-sm font-bold leading-relaxed bp-2xl:text-base [overflow-wrap:anywhere] [&_p]:my-2 [&_h1]:my-2 [&_h1]:font-semibold [&_h2]:my-2 [&_h2]:font-semibold [&_h3]:my-2 [&_h3]:font-semibold [&_ul]:mb-3 [&_ul]:ml-6 [&_ol]:mb-3 [&_ol]:ml-6 [&_code]:rounded [&_code]:border [&_code]:border-solid [&_code]:border-border [&_code]:bg-surface-muted [&_code]:px-1 [&_code]:py-1 [&_code]:text-text-primary [&_pre]:overflow-auto [&_pre]:rounded-lg [&_pre]:border [&_pre]:border-solid [&_pre]:border-border [&_pre]:bg-bg-primary [&_pre]:p-3 [&_pre]:text-text-primary";

const editBtnBase =
    "z-10 flex min-h-6 min-w-6 shrink-0 cursor-pointer touch-manipulation appearance-none items-center justify-center rounded border-none p-1 text-text-primary opacity-100 transition-[opacity,background-color] duration-200 [-webkit-tap-highlight-color:transparent] [@media(hover:hover)_and_(pointer:fine)]:opacity-70 [@media(hover:hover)_and_(pointer:fine)]:group-hover/msg:opacity-100 [@media(hover:none)_and_(pointer:coarse)]:min-h-8 [@media(hover:none)_and_(pointer:coarse)]:min-w-8 [@media(hover:none)_and_(pointer:coarse)]:p-1.5";

export const aiChatShell = {
    headerMain: "flex min-w-0 flex-1 flex-row items-center gap-3 [&_h2]:shrink-0",
    parameters:
        `inline-block w-fit max-w-full min-w-0 flex-[0_1_auto] box-border whitespace-normal break-words rounded-lg bg-surface-muted px-3 py-1 leading-snug ${typeShell.captionMuted}`,
    actions: "ml-auto shrink-0 gap-2",

    messages:
        "flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto overscroll-y-contain pr-1",
    empty: `flex min-h-0 flex-1 items-center justify-center overflow-hidden ${typeShell.hint}`,

    msg: msgBase,
    msgUser: `${msgBase} group/msg self-end border border-solid border-accent-30 bg-accent-15`,
    msgAi: `${msgBase} group/msg self-start border border-solid border-border bg-bg-primary`,
    msgCentered: "self-center my-auto text-center",
    msgContent,
    msgHeader: "mb-2 flex items-center justify-between gap-2",
    msgTurnBadge:
        "inline-block rounded bg-border-subtle px-1.5 py-1 text-left text-sm font-medium text-text-muted",
    msgTurnBadgeUser:
        "inline-block rounded bg-accent-10 px-1.5 py-1 text-left text-sm font-medium text-accent",
    msgEditButton: `${editBtnBase} bg-border-subtle hover:bg-surface-inverse`,
    msgEditButtonUser: `${editBtnBase} bg-accent-10 hover:bg-accent-20`,
    msgEditIcon: "material-icons text-icon-inline leading-none",

    formWrap: `z-[1] flex shrink-0 flex-col gap-2 bg-bg-primary font-sans ${typeShell.body} tracking-[0.01em] antialiased`,
    form: "flex w-full flex-col items-stretch gap-2 bp-md:flex-row",
    textarea:
        "!box-border !min-h-11 !py-2.5 max-h-[calc(1.6em*3+1.25rem)] min-w-0 flex-1 resize-none overflow-y-auto leading-normal text-base",
    formSubmit: "shrink-0 bp-md:min-h-11 bp-md:self-stretch",

    safetyNotice:
        `m-0 box-border flex w-full items-start gap-2 rounded-md bg-surface-muted px-3 py-1.5 font-sans antialiased ${typeShell.hint}`,
    safetyNoticeIcon:
        "material-icons mt-0.5 shrink-0 text-icon-control leading-none text-text-muted",
    safetyNoticeBody: "min-w-0 flex-1 [&_p]:m-0 [&_p+p]:mt-1.5",

    turnLimitMessage:
        `rounded-md border border-solid border-error-30 bg-error-10 p-2 text-center ${typeShell.body} text-error`,
    error: `mt-2 ${typeShell.body} text-error`,

    typing: "inline-flex items-center gap-2",
    typingLabel: `${typeShell.bodySemibold} bp-2xl:text-lg`,
    typingDots: "inline-flex gap-1",
    dot: "h-1.5 w-1.5 animate-typing-dot rounded-full bg-accent",
    dot2: "h-1.5 w-1.5 animate-typing-dot-2 rounded-full bg-accent",
    dot3: "h-1.5 w-1.5 animate-typing-dot-3 rounded-full bg-accent",

    modalMessagePreview: "mb-3 text-left",
    modalMessagePreviewContent:
        `max-h-[150px] overflow-y-auto whitespace-pre-wrap break-words rounded-lg border border-solid border-border bg-surface-muted p-3 leading-relaxed ${typeShell.muted}`,
    modalEditLabel: "mb-2 block",
    modalEditNotice:
        "mt-2 rounded bg-surface-muted p-2 text-sm text-text-muted",
    modalTurnInfo: "mb-3 rounded px-3 py-1.5 text-sm font-medium",
    modalTurnInfoSuccess:
        "mb-3 rounded border border-solid border-success-30 bg-success-15 px-3 py-1.5 text-sm font-medium text-button-success",
    modalTurnInfoInfo:
        "mb-3 rounded bg-accent-15 px-3 py-1.5 text-sm font-medium text-accent",
};
